import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Seva Sethu fallback page content (from Shopify export)
const sevaSethuPages = {
  about: {
    sections: [
      {
        sectionType: 'article',
        sectionId: 'about',
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: 'Our Story' },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: 'about-content',
            text: {
              fieldType: 'markdown',
              content: `
*What if shopping could be an act of service?*

In Sanskrit, **"seva"** means selfless service — giving without expecting anything in return. We built Seva Market to bring that spirit into everyday commerce. We're a community marketplace where artisans, small businesses, and makers sell products that matter. **No faceless corporations. No anonymous warehouses.** Just people serving people.

## Why We Exist

Big marketplaces prioritize profits over people. Sellers are anonymous. Quality is inconsistent. Money flows to distant shareholders. **We built something different.**

## How We're Different

| Traditional Marketplaces | Seva Sethu |
|--------------------------|------------|
| Anonymous sellers | Know who you're buying from |
| Race to bottom on price | Fair prices, fair wages |
| Profits leave community | Money stays local |

## Our Values

- **Community First** - Serve sellers & buyers, not shareholders
- **Trust & Transparency** - Every seller verified. Every product real.
- **Conscious Commerce** - Products for mindful living
- **Seva Spirit** - Every sale supports community causes

## For Buyers

Find products from people who care. Know exactly who you're supporting. Shop without compromise.

## For Sellers

Reach customers who value quality over cheapness. Keep more of what you earn. Be part of a community.

## Where Does the Money Go?

**100% of our net proceeds are donated to community causes:**

- Food distribution
- Community education
- Artisan family support
- Local temple programs

Every quarter, we publish exactly where the money went. Full transparency. No corporate profits. Just seva.

---

**Where every purchase is service.**

[Shop Now](/s) | [Become a Seller](/l/new)
              `,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: { fieldType: 'metaTitle', content: 'Our Story | Seva Sethu' },
      pageDescription: { fieldType: 'metaDescription', content: 'Learn about Seva Sethu - a community marketplace where every purchase is service. 100% of net proceeds donated to community causes.' },
    },
  },
  contact: {
    sections: [
      {
        sectionType: 'article',
        sectionId: 'contact',
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: 'Contact Us' },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: 'contact-content',
            text: {
              fieldType: 'markdown',
              content: `
We're here to help! If you have questions, feedback, or need assistance, please reach out to us.

## Email

**General Inquiries:** support@seva-sethu.com

## Response Time

We typically respond to all inquiries within 1-2 business days.

## For Sellers

If you're interested in becoming a seller on Seva Sethu, please visit our [Become a Seller](/l/new) page.

## Follow Us

Stay connected with our community:
- [Facebook](https://facebook.com/seva-sethu)
- [Instagram](https://instagram.com/seva-sethu)

## Frequently Asked Questions

Have a question? Check our [FAQ page](/p/faq) for quick answers to common questions.
              `,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: { fieldType: 'metaTitle', content: 'Contact Us | Seva Sethu' },
      pageDescription: { fieldType: 'metaDescription', content: 'Contact Seva Sethu marketplace. We are here to help with questions about buying, selling, or using our platform.' },
    },
  },
  faq: {
    sections: [
      {
        sectionType: 'article',
        sectionId: 'faq',
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: 'Frequently Asked Questions' },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: 'faq-content',
            text: {
              fieldType: 'markdown',
              content: `
## Shopping

### How do I place an order?
Browse our products, add items to your cart, and proceed to checkout. You can pay securely using various payment methods.

### What payment methods do you accept?
We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and Shop Pay.

### Can I modify or cancel my order?
Please contact us immediately after placing your order. We can only modify or cancel orders that haven't been shipped yet.

## Shipping

### How long does shipping take?
Shipping times vary by seller and location. Most orders are delivered within 5-10 business days. You'll receive tracking information once your order ships.

### Do you ship internationally?
Shipping availability depends on the individual seller. Please check the product page or contact the seller for international shipping options.

## Returns & Refunds

### What is your return policy?
Most items can be returned within 30 days of delivery. Items must be unused and in original packaging. Please see our [Terms of Service](/terms-of-service) for full details.

### How do I request a refund?
Contact us through our [Contact page](/p/contact) with your order number. Refunds are processed within 5-7 business days after we receive the returned item.

## For Sellers

### How do I become a seller?
Visit our [Become a Seller](/l/new) page to register. Once approved, you can start listing your products.

### What are the seller fees?
Please contact us for information about our seller fee structure and commission rates.

---

**Can't find what you're looking for?**

[Contact Us](/p/contact)
              `,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: { fieldType: 'metaTitle', content: 'FAQ | Seva Sethu' },
      pageDescription: { fieldType: 'metaDescription', content: 'Frequently asked questions about shopping, selling, shipping, and returns on Seva Sethu marketplace.' },
    },
  },
  'shipping-returns': {
    sections: [
      {
        sectionType: 'article',
        sectionId: 'shipping-returns',
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: 'Shipping & Returns' },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: 'shipping-returns-content',
            text: {
              fieldType: 'markdown',
              content: `
## Shipping Information

### Processing Time
Orders are typically processed within 1-3 business days. You will receive a confirmation email with tracking information once your order ships.

### Delivery Times
- **Standard Shipping:** 5-10 business days
- **Express Shipping:** 2-3 business days (where available)
- **Local Pickup:** Available for select sellers

### Shipping Costs
Shipping costs are calculated at checkout based on your location and the seller shipping rates. Some sellers offer free shipping on orders over a certain amount.

---

## Return Policy

### Return Window
Most items can be returned within **30 days** of delivery for a full refund or exchange.

### Return Conditions
- Items must be unused and in original condition
- Original packaging and tags must be intact
- Proof of purchase is required

### Non-Returnable Items
- Perishable goods (food items, prasadam)
- Customized or personalized items
- Digital products and services
- Items marked as final sale

---

## Refund Process

### How to Request a Return
1. Contact us through our [Contact page](/p/contact) with your order number
2. We will provide you with return instructions and a return shipping label (if applicable)
3. Ship the item back within 7 days of receiving return authorization
4. Refund will be processed within 5-7 business days after we receive the item

### Refund Method
Refunds will be issued to the original payment method. Please allow 5-10 business days for the refund to appear in your account.

---

**Have questions about your order?**

[Contact Us](/p/contact)
              `,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: { fieldType: 'metaTitle', content: 'Shipping & Returns | Seva Sethu' },
      pageDescription: { fieldType: 'metaDescription', content: 'Shipping information and return policy for Seva Sethu marketplace.' },
    },
  },
  help: {
    sections: [
      {
        sectionType: 'article',
        sectionId: 'help',
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: 'Help Center' },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: 'help-content',
            text: {
              fieldType: 'markdown',
              content: `
## How Can We Help?

### Quick Links

- [FAQ](/p/faq) - Find answers to common questions
- [Shipping & Returns](/p/shipping-returns) - Learn about delivery and return policies
- [Contact Us](/p/contact) - Get in touch with our support team
- [Become a Seller](/l/new) - Start selling on our marketplace

### Information Pages

- [Privacy Policy](/privacy-policy)
- [Terms of Service](/terms-of-service)
- [About Us](/p/about)

---

**Still Need Help?**

Our support team is here to assist you.

[Contact Support](/p/contact)
              `,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: { fieldType: 'metaTitle', content: 'Help Center | Seva Sethu' },
      pageDescription: { fieldType: 'metaDescription', content: 'Seva Sethu Help Center - Find answers to common questions and get support.' },
    },
  },
};

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error } = props;
  const pageId = params.pageId || props.pageId;

  // Check if we have hosted asset data for this page
  const hostedData = pageAssetsData?.[pageId]?.data;

  // Use hosted data if available, otherwise check for Seva Sethu fallback
  const pageData = hostedData || sevaSethuPages[pageId];

  if (!inProgress && !pageData && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  return (
    <PageBuilder
      pageAssetsData={pageData}
      inProgress={inProgress}
      schemaType="Article"
    />
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(
  withRouter,
  connect(mapStateToProps)
)(CMSPageComponent);

export default CMSPage;
